import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { adminSubscriptionsAPI } from '../../../lib/api';
import {
  formatCoef,
  formatMoney,
  formatSignedMoney,
  maskCardRequisites,
  moneyPrice,
  monthRange,
  nextChargeDateFromPeriod,
  normalizeDecimal,
  sumSignedMoney,
  todayISO,
} from '../../../lib/subscriptionBilling';
import { useToast } from '../../../hooks/use-toast';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';

const emptyChargeForm = () => ({
  charge_date: todayISO(),
  client_id: '',
  service_id: '',
  tariff_id: '',
  partner_id: '',
  charge_price: '',
  exchange_rate: '',
  billing_period: '',
  next_charge_date: '',
  is_active: false,
  notify_email: '',
  manager_email: '',
});

const PERIOD_LABELS = { month: 'Раз в месяц', year: 'Раз в год', once: 'Разово' };

function formatDate(iso) {
  if (!iso || !iso.includes('-')) return iso || '';
  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
}

function sumMoney(values) {
  const cents = values.reduce((acc, value) => {
    const text = moneyPrice(value, '1');
    if (!text) return acc;
    return acc + BigInt(text.replace('.', ''));
  }, 0n);
  const text = cents.toString().padStart(3, '0');
  return formatMoney(`${text.slice(0, -2)}.${text.slice(-2)}`);
}

function activeOrCurrent(items, currentId) {
  return items.filter((item) => item.is_active || String(item.id) === String(currentId));
}

const SubscriptionJournal = () => {
  const { toast } = useToast();
  const initialRange = monthRange();
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [services, setServices] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [charges, setCharges] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: initialRange.dateFrom,
    dateTo: initialRange.dateTo,
    companyId: 'all',
    clientId: 'all',
    partnerId: 'all',
    serviceId: 'all',
  });
  const [exportUserIds, setExportUserIds] = useState([]);
  const [form, setForm] = useState(emptyChargeForm);
  const [editingId, setEditingId] = useState(null);
  const [storedPrices, setStoredPrices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDirectories = useCallback(async () => {
    const [companyRows, clientRows, partnerRows, serviceRows, tariffRows] = await Promise.all([
      adminSubscriptionsAPI.getCompanies(),
      adminSubscriptionsAPI.getClients(),
      adminSubscriptionsAPI.getPartners(),
      adminSubscriptionsAPI.getServices(),
      adminSubscriptionsAPI.getTariffs(),
    ]);
    setCompanies(companyRows || []);
    setClients(clientRows || []);
    setPartners(partnerRows || []);
    setServices(serviceRows || []);
    setTariffs(tariffRows || []);
  }, []);

  const loadCharges = useCallback(async () => {
    const params = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.companyId !== 'all') params.company_id = filters.companyId;
    if (filters.clientId !== 'all') params.client_id = filters.clientId;
    if (filters.partnerId !== 'all') params.partner_id = filters.partnerId;
    if (filters.serviceId !== 'all') params.service_id = filters.serviceId;
    const rows = await adminSubscriptionsAPI.getCharges(params);
    setCharges(rows || []);
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    loadDirectories().catch((error) => {
      if (!cancelled) {
        toast({ title: 'Ошибка загрузки', description: error.message, variant: 'destructive' });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loadDirectories, toast]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCharges()
      .catch((error) => {
        if (!cancelled) {
          toast({ title: 'Ошибка загрузки', description: error.message, variant: 'destructive' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadCharges, toast]);

  const selectedClient = clients.find((item) => String(item.id) === String(form.client_id));
  const selectedCompany = companies.find((item) => item.id === selectedClient?.company_id);
  const balanceClients = filters.companyId === 'all'
    ? clients
    : clients.filter((item) => String(item.company_id) === String(filters.companyId));
  const balanceToday = formatSignedMoney(sumSignedMoney(balanceClients.map((item) => item.balance || '0')));
  const selectedPartner = partners.find((item) => String(item.id) === String(form.partner_id));
  const tariffsForService = useMemo(
    () => tariffs.filter((item) => String(item.service_id) === String(form.service_id)),
    [tariffs, form.service_id]
  );
  const exportUsers = useMemo(() => {
    const companyClients = filters.companyId === 'all'
      ? clients
      : clients.filter((item) => String(item.company_id) === String(filters.companyId));
    const map = new Map();
    companyClients.forEach((item) => {
      if (item.user_id) {
        map.set(item.user_id, item.user_email || item.user_id);
      }
    });
    return Array.from(map.entries()).map(([id, email]) => ({ id, email }));
  }, [clients, filters.companyId]);

  const previewPartnerPrice = selectedPartner
    ? moneyPrice(form.charge_price, selectedPartner.partner_coefficient)
    : null;
  const previewClientPrice = selectedCompany
    ? moneyPrice(form.charge_price, selectedCompany.client_coefficient)
    : null;
  const previewRubPrice = previewClientPrice
    ? moneyPrice(previewClientPrice, form.exchange_rate)
    : null;
  const pricesWillChange = Boolean(
    editingId
    && storedPrices
    && previewPartnerPrice
    && previewClientPrice
    && previewRubPrice
    && (
      previewPartnerPrice !== storedPrices.partner_price
      || previewClientPrice !== storedPrices.client_price
      || previewRubPrice !== storedPrices.rub_price
    )
  );

  const resetForm = () => {
    setEditingId(null);
    setStoredPrices(null);
    setForm(emptyChargeForm());
  };

  const onServiceChange = (value) => {
    setForm((prev) => ({ ...prev, service_id: value, tariff_id: '' }));
  };

  const onTariffChange = (value) => {
    const tariff = tariffs.find((item) => String(item.id) === value);
    setForm((prev) => ({
      ...prev,
      tariff_id: value,
      charge_price: tariff?.default_charge_price || prev.charge_price,
    }));
  };

  const onChargeDateChange = (value) => {
    setForm((prev) => ({
      ...prev,
      charge_date: value,
      next_charge_date: nextChargeDateFromPeriod(value, prev.billing_period),
    }));
  };

  const onBillingPeriodChange = (value) => {
    const period = value === 'none' ? '' : value;
    setForm((prev) => ({
      ...prev,
      billing_period: period,
      next_charge_date: nextChargeDateFromPeriod(prev.charge_date, period),
    }));
  };

  const saveCharge = async (event) => {
    event.preventDefault();
    if (!form.client_id || !form.service_id || !form.tariff_id || !form.partner_id || !form.charge_date) {
      toast({ title: 'Заполните клиента, сервис, тариф, партнёра и дату', variant: 'destructive' });
      return;
    }
    if (moneyPrice(form.charge_price, '1') === null) {
      toast({ title: 'Укажите цену списания', variant: 'destructive' });
      return;
    }
    if (moneyPrice('1', form.exchange_rate) === null) {
      toast({ title: 'Укажите обменный курс', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await adminSubscriptionsAPI.saveCharge(editingId, {
        charge_date: form.charge_date,
        client_id: Number(form.client_id),
        partner_id: Number(form.partner_id),
        service_id: Number(form.service_id),
        tariff_id: Number(form.tariff_id),
        charge_price: normalizeDecimal(form.charge_price),
        exchange_rate: normalizeDecimal(form.exchange_rate),
        billing_period: form.billing_period || null,
        next_charge_date: form.next_charge_date || null,
        is_active: form.is_active,
        notify_email: form.notify_email,
        manager_email: form.manager_email,
      });
      toast({ title: editingId ? 'Списание обновлено' : 'Списание записано' });
      resetForm();
      await loadCharges();
    } catch (error) {
      toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const editCharge = (row) => {
    setEditingId(row.id);
    setStoredPrices({
      partner_price: row.partner_price,
      client_price: row.client_price,
      rub_price: row.rub_price,
    });
    setForm({
      charge_date: row.charge_date,
      client_id: String(row.client_id),
      service_id: String(row.service_id),
      tariff_id: String(row.tariff_id),
      partner_id: String(row.partner_id),
      charge_price: row.charge_price,
      exchange_rate: row.exchange_rate,
      billing_period: row.billing_period || '',
      next_charge_date: row.next_charge_date || '',
      is_active: Boolean(row.is_active),
      notify_email: row.notify_email || '',
      manager_email: row.manager_email || '',
    });
  };

  const removeCharge = async (row) => {
    if (!window.confirm(`Переместить списание ${row.charge_date} / ${row.client_name} / ${row.service_name} в корзину?`)) return;
    try {
      await adminSubscriptionsAPI.deleteCharge(row.id);
      if (editingId === row.id) resetForm();
      await loadCharges();
    } catch (error) {
      toast({ title: 'Не удалось удалить', description: error.message, variant: 'destructive' });
    }
  };

  const exportFile = async (kind) => {
    if (!filters.dateFrom || !filters.dateTo) {
      toast({ title: 'Укажите период', variant: 'destructive' });
      return;
    }
    try {
      if (kind === 'client') {
        if (filters.companyId === 'all') {
          toast({ title: 'Выберите компанию', variant: 'destructive' });
          return;
        }
        const params = {
          company_id: filters.companyId,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
        };
        if (filters.clientId !== 'all') params.client_id = filters.clientId;
        if (exportUserIds.length === 1) params.user_id = exportUserIds[0];
        if (exportUserIds.length > 1) params.user_ids = exportUserIds;
        await adminSubscriptionsAPI.exportClient(params);
      } else {
        if (filters.partnerId === 'all') {
          toast({ title: 'Выберите партнёра', variant: 'destructive' });
          return;
        }
        await adminSubscriptionsAPI.exportPartner({
          partner_id: filters.partnerId,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
        });
      }
    } catch (error) {
      toast({ title: 'Экспорт не удался', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{editingId ? 'Исправить списание' : 'Новое списание'}</CardTitle>
          <CardDescription>
            Цена партнёра и цена клиента считаются при сохранении и остаются в строке, даже если коэффициент потом изменится.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveCharge} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Дата</Label>
                <Input
                  type="date"
                  value={form.charge_date}
                  onChange={(event) => onChargeDateChange(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Клиент</Label>
                <Select value={form.client_id || undefined} onValueChange={(value) => setForm((prev) => ({ ...prev, client_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Выберите клиента" /></SelectTrigger>
                  <SelectContent>
                    {activeOrCurrent(clients, form.client_id).map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name} · {item.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Партнёр</Label>
                <Select value={form.partner_id || undefined} onValueChange={(value) => setForm((prev) => ({ ...prev, partner_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Выберите партнёра" /></SelectTrigger>
                  <SelectContent>
                    {activeOrCurrent(partners, form.partner_id).map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Сервис</Label>
                <Select value={form.service_id || undefined} onValueChange={onServiceChange}>
                  <SelectTrigger><SelectValue placeholder="Выберите сервис" /></SelectTrigger>
                  <SelectContent>
                    {activeOrCurrent(services, form.service_id).map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Тариф</Label>
                <Select value={form.tariff_id || undefined} onValueChange={onTariffChange} disabled={!form.service_id}>
                  <SelectTrigger><SelectValue placeholder="Выберите тариф" /></SelectTrigger>
                  <SelectContent>
                    {activeOrCurrent(tariffsForService, form.tariff_id).map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Цена списания</Label>
                <Input
                  inputMode="decimal"
                  value={form.charge_price}
                  onChange={(event) => setForm((prev) => ({ ...prev, charge_price: event.target.value }))}
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-2">
                <Label>Обменный курс</Label>
                <Input
                  inputMode="decimal"
                  value={form.exchange_rate}
                  onChange={(event) => setForm((prev) => ({ ...prev, exchange_rate: event.target.value }))}
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-2">
                <Label>Период оплаты</Label>
                <Select
                  value={form.billing_period || 'none'}
                  onValueChange={onBillingPeriodChange}
                >
                  <SelectTrigger><SelectValue placeholder="Не задан" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Не задан</SelectItem>
                    <SelectItem value="month">Раз в месяц</SelectItem>
                    <SelectItem value="year">Раз в год</SelectItem>
                    <SelectItem value="once">Разово</SelectItem>
                  </SelectContent>
                </Select>
                {form.billing_period === 'once' && (
                  <p className="text-xs text-muted-foreground">
                    Разовая покупка (например, кредиты): уведомления о продлении не отправляются.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Дата следующего списания</Label>
                <Input
                  type="date"
                  value={form.next_charge_date}
                  onChange={(event) => setForm((prev) => ({ ...prev, next_charge_date: event.target.value }))}
                  disabled={form.billing_period === 'once'}
                />
                {(form.billing_period === 'month' || form.billing_period === 'year') && (
                  <p className="text-xs text-muted-foreground">
                    Считается автоматически: тот же день через месяц или год от даты списания.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Email клиента</Label>
                <Input
                  type="email"
                  value={form.notify_email}
                  onChange={(event) => setForm((prev) => ({ ...prev, notify_email: event.target.value }))}
                  placeholder="client@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Email менеджера Антологии</Label>
                <Input
                  type="email"
                  value={form.manager_email}
                  onChange={(event) => setForm((prev) => ({ ...prev, manager_email: event.target.value }))}
                  placeholder="manager@antologia.tech"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.is_active}
                    onCheckedChange={(value) => setForm((prev) => ({ ...prev, is_active: value === true }))}
                  />
                  Лицензия активна (уведомления)
                </label>
              </div>
            </div>

            <div className="grid gap-3 rounded-md border bg-muted/40 p-4 text-sm md:grid-cols-2">
              <div>
                <div className="text-muted-foreground">Компания</div>
                <div>{selectedCompany ? `${selectedCompany.name} · коэф. ${formatCoef(selectedCompany.client_coefficient)}` : '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Баланс клиента</div>
                <div className="text-base font-medium">{selectedClient ? formatSignedMoney(selectedClient.balance || '0') : '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Цена клиента</div>
                <div className="text-base font-medium">{previewClientPrice ? formatMoney(previewClientPrice) : '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Обменный курс</div>
                <div>{form.exchange_rate ? formatCoef(normalizeDecimal(form.exchange_rate)) : '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Цена в рублях</div>
                <div className="text-base font-medium">{previewRubPrice ? formatMoney(previewRubPrice) : '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Реквизиты партнёра</div>
                <div className="break-all">{selectedPartner?.card_details || '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Платёжный адрес</div>
                <div className="break-all">{selectedPartner?.payment_address || '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Коэффициент партнёра</div>
                <div>{selectedPartner ? formatCoef(selectedPartner.partner_coefficient) : '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Цена партнёра</div>
                <div className="text-base font-medium">{previewPartnerPrice ? formatMoney(previewPartnerPrice) : '—'}</div>
              </div>
            </div>
            {pricesWillChange && (
              <p className="text-sm text-amber-700">
                При сохранении цены пересчитаются по текущим коэффициентам. В журнале до сохранения останутся прежние суммы.
              </p>
            )}

            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить списание'}</Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>Отмена</Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Журнал</CardTitle>
          <CardDescription>Период по умолчанию — текущий месяц. Экспорт берёт компанию или партнёра и даты из этого фильтра.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Input type="date" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} aria-label="Период с" />
            <Input type="date" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} aria-label="Период по" />
            <Select value={filters.companyId} onValueChange={(value) => setFilters((prev) => ({ ...prev, companyId: value }))}>
              <SelectTrigger aria-label="Компания"><SelectValue placeholder="Компания" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все компании</SelectItem>
                {companies.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.clientId} onValueChange={(value) => setFilters((prev) => ({ ...prev, clientId: value }))}>
              <SelectTrigger aria-label="Клиент"><SelectValue placeholder="Клиент" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все клиенты</SelectItem>
                {clients.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.partnerId} onValueChange={(value) => setFilters((prev) => ({ ...prev, partnerId: value }))}>
              <SelectTrigger aria-label="Партнёр"><SelectValue placeholder="Партнёр" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все партнёры</SelectItem>
                {partners.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.serviceId} onValueChange={(value) => setFilters((prev) => ({ ...prev, serviceId: value }))}>
              <SelectTrigger aria-label="Сервис"><SelectValue placeholder="Сервис" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все сервисы</SelectItem>
                {services.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {exportUsers.length > 0 && (
            <div className="rounded-md border p-3 space-y-2">
              <p className="text-sm font-medium">Пользователи ЛК для выгрузки (пусто = вся компания)</p>
              <div className="flex flex-wrap gap-3">
                {exportUsers.map((item) => {
                  const checked = exportUserIds.includes(item.id);
                  return (
                    <label key={item.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => {
                          setExportUserIds((prev) => (
                            value === true
                              ? [...prev, item.id]
                              : prev.filter((id) => id !== item.id)
                          ));
                        }}
                      />
                      {item.email}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => exportFile('client')}>Excel для клиента</Button>
            <Button type="button" variant="outline" onClick={() => exportFile('partner')}>Excel для партнёра</Button>
          </div>
          <p className="text-sm">Баланс на сегодня: {balanceToday}</p>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Дата</TableHead>
                  <TableHead>Клиент</TableHead>
                  <TableHead>Компания</TableHead>
                  <TableHead>Сервис</TableHead>
                  <TableHead>Тариф</TableHead>
                  <TableHead>Партнёр</TableHead>
                  <TableHead>Реквизиты</TableHead>
                  <TableHead>Платёжный адрес</TableHead>
                  <TableHead>Цена списания</TableHead>
                  <TableHead>Цена партнёра</TableHead>
                  <TableHead>Цена клиента</TableHead>
                  <TableHead>Обменный курс</TableHead>
                  <TableHead>Цена в рублях</TableHead>
                  <TableHead>Период</TableHead>
                  <TableHead>След. списание</TableHead>
                  <TableHead>Активна</TableHead>
                  <TableHead>Менеджер</TableHead>
                  <TableHead>Email клиента</TableHead>
                  <TableHead>Email менеджера</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={20}>Загрузка...</TableCell></TableRow>
                )}
                {!loading && charges.length === 0 && (
                  <TableRow><TableCell colSpan={20}>За выбранный период списаний нет.</TableCell></TableRow>
                )}
                {!loading && charges.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDate(row.charge_date)}</TableCell>
                    <TableCell>{row.client_name}</TableCell>
                    <TableCell>{row.company_name}</TableCell>
                    <TableCell>{row.service_name}</TableCell>
                    <TableCell>{row.tariff_name}</TableCell>
                    <TableCell>{row.partner_name}</TableCell>
                    <TableCell className="max-w-[180px] break-all">{maskCardRequisites(row.card_details)}</TableCell>
                    <TableCell className="max-w-[180px] break-all">{row.payment_address}</TableCell>
                    <TableCell>{formatMoney(row.charge_price)}</TableCell>
                    <TableCell>{formatMoney(row.partner_price)}</TableCell>
                    <TableCell>{formatMoney(row.client_price)}</TableCell>
                    <TableCell>{formatCoef(row.exchange_rate)}</TableCell>
                    <TableCell>{formatMoney(row.rub_price)}</TableCell>
                    <TableCell>{PERIOD_LABELS[row.billing_period] || '—'}</TableCell>
                    <TableCell>{formatDate(row.next_charge_date)}</TableCell>
                    <TableCell>{row.is_active ? 'да' : 'нет'}</TableCell>
                    <TableCell>{row.purchased_by_manager ? 'да' : 'нет'}</TableCell>
                    <TableCell className="max-w-[160px] break-all">{row.notify_email || '—'}</TableCell>
                    <TableCell className="max-w-[160px] break-all">{row.manager_email || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button type="button" size="icon" variant="ghost" onClick={() => editCharge(row)} aria-label="Исправить">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeCharge(row)} aria-label="Удалить">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {charges.length > 0 && (
            <p className="text-sm text-muted-foreground">
              В выборке: списание {sumMoney(charges.map((row) => row.charge_price))}
              , партнёр {sumMoney(charges.map((row) => row.partner_price))}
              , клиент {sumMoney(charges.map((row) => row.client_price))}
              , рубли {sumMoney(charges.map((row) => row.rub_price))}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionJournal;
